// ═══════════════════════════════════════════════════════════════════════════════
// Shared recurrence field schemas & refinements
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { MONTHLY_KIND, RECURRENCE_TYPE } from '../constants/index.js';
import { dateSchema } from './common.schema.js';
export const recurrenceTypeSchema = z.enum([
    RECURRENCE_TYPE.NONE,
    RECURRENCE_TYPE.DAILY,
    RECURRENCE_TYPE.WEEKLY,
    RECURRENCE_TYPE.BIWEEKLY,
    RECURRENCE_TYPE.MONTHLY,
]);
export const monthlyKindSchema = z.enum([
    MONTHLY_KIND.DAY,
    MONTHLY_KIND.NTH_WEEKDAY,
    MONTHLY_KIND.LAST_WEEKDAY,
]);
export const recurrenceDaysSchema = z.array(z.number().int().min(0).max(6)).max(7).default([]);
export const recurrenceMonthDaysSchema = z
    .array(z.number().int().min(1).max(31))
    .max(31)
    .default([]);
export const recurrenceOrdinalSchema = z.number().int().min(1).max(5).nullable().default(null);
/** Weekly / biweekly / monthly weekday rules need at least one weekday. */
export const refineWeekdaySelection = (data, ctx) => {
    if ((data.recurrence === RECURRENCE_TYPE.WEEKLY ||
        data.recurrence === RECURRENCE_TYPE.BIWEEKLY) &&
        data.recurrenceDays.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Weekly/biweekly recurrence requires at least one day selected',
            path: ['recurrenceDays'],
        });
    }
    if (data.recurrence !== RECURRENCE_TYPE.MONTHLY) {
        return;
    }
    const kind = data.monthlyKind ?? '';
    if (kind !== MONTHLY_KIND.DAY &&
        kind !== MONTHLY_KIND.NTH_WEEKDAY &&
        kind !== MONTHLY_KIND.LAST_WEEKDAY) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Monthly recurrence requires monthlyKind',
            path: ['monthlyKind'],
        });
        return;
    }
    if (kind === MONTHLY_KIND.DAY) {
        if (!data.recurrenceMonthDays || data.recurrenceMonthDays.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Monthly day recurrence requires at least one day of month',
                path: ['recurrenceMonthDays'],
            });
        }
        return;
    }
    if (data.recurrenceDays.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Monthly weekday recurrence requires at least one weekday',
            path: ['recurrenceDays'],
        });
    }
    if (kind === MONTHLY_KIND.NTH_WEEKDAY) {
        if (data.recurrenceOrdinal == null || data.recurrenceOrdinal < 1 || data.recurrenceOrdinal > 5) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Monthly nth-weekday requires ordinal 1–5',
                path: ['recurrenceOrdinal'],
            });
        }
    }
};
export const refineOccurrenceOrStartDates = (data, ctx, labels) => {
    if (data.recurrence === RECURRENCE_TYPE.NONE) {
        if (!dateSchema.safeParse(data.occurrenceDate).success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: labels?.oneTime ?? 'One-time task requires occurrence date (YYYY-MM-DD)',
                path: ['occurrenceDate'],
            });
        }
        return;
    }
    if (!dateSchema.safeParse(data.startDate).success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: labels?.recurring ?? 'Recurring task requires start date (YYYY-MM-DD)',
            path: ['startDate'],
        });
    }
    if (data.endDate !== '' && !dateSchema.safeParse(data.endDate).success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be YYYY-MM-DD or empty',
            path: ['endDate'],
        });
    }
    if (dateSchema.safeParse(data.startDate).success &&
        dateSchema.safeParse(data.endDate).success &&
        data.endDate !== '' &&
        data.startDate > data.endDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be on or after start date',
            path: ['endDate'],
        });
    }
};
/** Normalize monthly-related columns for DB insert/update from validated API input. */
export const normalizeMonthlyColumnsForStorage = (input) => {
    if (input.recurrence !== RECURRENCE_TYPE.MONTHLY) {
        return {
            monthlyKind: null,
            recurrenceDays: input.recurrence === RECURRENCE_TYPE.WEEKLY ||
                input.recurrence === RECURRENCE_TYPE.BIWEEKLY
                ? [...input.recurrenceDays].sort((a, b) => a - b)
                : [],
            recurrenceMonthDays: [],
            recurrenceOrdinal: null,
        };
    }
    const kind = input.monthlyKind ?? MONTHLY_KIND.DAY;
    if (kind === MONTHLY_KIND.DAY) {
        const uniqueDays = [...new Set(input.recurrenceMonthDays ?? [])]
            .filter((d) => d >= 1 && d <= 31)
            .sort((a, b) => a - b);
        return {
            monthlyKind: MONTHLY_KIND.DAY,
            recurrenceDays: [],
            recurrenceMonthDays: uniqueDays,
            recurrenceOrdinal: null,
        };
    }
    const weekdays = [...new Set(input.recurrenceDays)]
        .filter((d) => d >= 0 && d <= 6)
        .sort((a, b) => a - b);
    if (kind === MONTHLY_KIND.NTH_WEEKDAY) {
        return {
            monthlyKind: MONTHLY_KIND.NTH_WEEKDAY,
            recurrenceDays: weekdays,
            recurrenceMonthDays: [],
            recurrenceOrdinal: input.recurrenceOrdinal ?? 1,
        };
    }
    return {
        monthlyKind: MONTHLY_KIND.LAST_WEEKDAY,
        recurrenceDays: weekdays,
        recurrenceMonthDays: [],
        recurrenceOrdinal: null,
    };
};
//# sourceMappingURL=recurrence.schema.js.map